import React, { useCallback, useEffect, useRef, useState } from 'react'

import { Box, Stack, Typography } from '@mui/material'
import { ImageIcon } from 'lucide-react'
import jsQR from 'jsqr'

import { DrawerPopup } from './DrawerPopup'
import { menuRowToneSx } from './menuRowTone'
import { PICKER_SHEET } from './pickerListStyle'

/**
 * VRG-358 — scan a person's Vera code instead of typing their name.
 *
 * The point of this component is the interaction model, not the pixels: picking a
 * recipient by typing a name is RECALL (you type "John", you get several, you pick
 * one and hope), and a gift in this epic's own verification went to the wrong
 * "John Doe" that way. Scanning a code the recipient is holding is POSSESSION —
 * there is exactly one answer and the recipient chose it.
 *
 * TWO input paths, ONE decoder. Both are required:
 *
 *  1. LIVE CAMERA — `getUserMedia`, frames drawn to an offscreen canvas and
 *     decoded on a `requestAnimationFrame` loop.
 *  2. STILL IMAGE — `<input type="file" accept="image/*" capture="environment">`.
 *     On a phone that opens the camera directly; on desktop it is a file picker.
 *
 * Path 2 is not a nicety. This app is phone-first and iOS-heavy (it is reached by
 * tapping an NFC tag with a phone), and the live path can fail for reasons the user
 * cannot fix in the moment: permission previously denied, an in-app browser that
 * does not grant camera, an insecure origin, or simply a screenshot of the code
 * sent over chat. A scanner with only path 1 is a dead end in every one of those.
 *
 * Decoding is jsQR in BOTH paths, deliberately, and NOT the native
 * `BarcodeDetector`: Safari does not implement it, so a BarcodeDetector-based
 * scanner works on Android and silently fails on the iPhone that is most of this
 * app's traffic. `decodeImageData` is the single shared decode so the two paths
 * cannot drift into decoding differently.
 */

/**
 * Longest edge, in px, that a frame is scaled to before decoding.
 *
 * jsQR walks every pixel, so decode cost is linear in area. A 1920x1080 frame is
 * ~2.07M pixels; scaled to a 400px longest edge it is ~90K — roughly 23x less work
 * per frame. 400 still leaves a QR occupying a third of the frame at ~130px, far
 * above the module size jsQR needs to lock on.
 */
const MAX_DECODE_EDGE = 400

/**
 * Decode at ~10fps rather than at the display refresh rate.
 *
 * The loop is driven by `requestAnimationFrame` (so it pauses when the tab is
 * hidden, which a `setInterval` would not), but decoding on every one of those
 * ~60 callbacks is 6x the work for no gain: nobody moves a phone over a code fast
 * enough for 100ms of latency to be perceptible, and the dropped frames are what
 * keep the video preview itself smooth.
 */
const DECODE_INTERVAL_MS = 100

/** `HTMLMediaElement.HAVE_CURRENT_DATA` — the first state with a drawable frame. */
const HAVE_CURRENT_DATA = 2

export type QrScannerViewProps = {
  /**
   * Whether the camera should be RUNNING. Not "is it visible": a scanner in a
   * hidden tab panel is mounted but must not hold the camera, and the hardware
   * indicator staying lit behind another tab is the exact bug this distinction
   * prevents.
   */
  active: boolean
  /**
   * The raw decoded string. It is handed over verbatim, un-parsed: this component
   * knows how to read a QR code, not what a Vera code looks like. The caller owns
   * interpretation (a bare code, a `https://host/u/<code>` URL, or something that
   * is neither and should be rejected).
   */
  onDecoded: (raw: string) => void
  /**
   * A message from the CALLER about the last decode — "that code didn't match
   * anyone" and the like. Rendered in this component's own inline message slot,
   * so the surface has ONE place errors appear rather than the page raising a
   * toast over a scanner that is already showing its own.
   *
   * PRESENTATIONAL ONLY — it does not resume scanning. A decode stops the
   * camera (see `handleDecoded`), so a caller that REJECTS a code must also
   * remount this component to get the viewfinder back, which React's `key` does
   * in one line:
   *
   *     <QrScannerView key={attempt} notice={notice} … />
   *
   * Deliberately not driven off `notice` changing. Rescanning the same bad code
   * sets the same string, React bails out of the re-render, and an effect
   * watching the prop never runs — so the second attempt would silently leave
   * the camera dead. A counter the caller bumps on every rejection cannot have
   * that hole.
   */
  notice?: string | null
}

export type QrScannerProps = {
  open: boolean
  onClose: () => void
  onDecoded: (raw: string) => void
  /** See `QrScannerViewProps.notice`. */
  notice?: string | null
}

/**
 * The one decode. Both input paths funnel through here so a fix or a tuning change
 * to one can never leave the other behind.
 */
const decodeImageData = (imageData: ImageData): string | null => {
  const result = jsQR(imageData.data, imageData.width, imageData.height)
  const raw = result?.data?.trim()
  return raw ? raw : null
}

/**
 * Draw any drawable source into `canvas` at a decode-friendly size and return its
 * pixels. Shared by the camera loop and the still-image path so the downscale rule
 * is applied identically to both.
 */
const drawScaledToImageData = (
  source: CanvasImageSource,
  sourceWidth: number,
  sourceHeight: number,
  canvas: HTMLCanvasElement,
): ImageData | null => {
  if (!sourceWidth || !sourceHeight) return null

  const scale = Math.min(1, MAX_DECODE_EDGE / Math.max(sourceWidth, sourceHeight))
  const width = Math.max(1, Math.round(sourceWidth * scale))
  const height = Math.max(1, Math.round(sourceHeight * scale))

  canvas.width = width
  canvas.height = height

  // `willReadFrequently` tells the browser to keep this canvas on the CPU. Without
  // it, every `getImageData` on a GPU-backed canvas is a readback stall — the exact
  // thing we do 10x a second.
  const ctx = canvas.getContext('2d', { willReadFrequently: true })
  if (!ctx) return null

  ctx.drawImage(source, 0, 0, width, height)
  return ctx.getImageData(0, 0, width, height)
}

/** Load a picked file as an <img> we can draw. Object URL is revoked either way. */
const loadImageElement = (file: File): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => {
      URL.revokeObjectURL(url)
      resolve(img)
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('Image could not be decoded'))
    }
    img.src = url
  })

/**
 * The scanner SURFACE — viewfinder, instruction line, photo fallback — with no
 * container of its own.
 *
 * Split out (VERA-304) so the code page can render the scanner INLINE under a
 * segmented control while the gift picker keeps presenting it as a sheet. The
 * alternative was a second scanner for the inline case, which is the drift
 * working-conventions §36 exists to prevent: two camera lifecycles, two decode
 * paths, two places to fix an iOS quirk. Everything below is the original
 * component verbatim; only the `open` prop became `active` and the DrawerPopup
 * moved out to the wrapper.
 */
export const QrScannerView = ({ active: open, onDecoded, notice }: QrScannerViewProps) => {
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const rafRef = useRef<number | null>(null)
  const lastDecodeRef = useRef(0)

  /**
   * A QR sits in frame for many decode ticks, so without this the loop fires
   * `onDecoded` ~10 times a second for the same code — which, on the consuming
   * side, is ~10 lookups and 10 recipient selections for one scan.
   */
  const handledRef = useRef(false)

  // `onDecoded` is read through a ref so a parent re-render that produces a new
  // callback identity does not tear down and re-acquire the camera mid-scan.
  const onDecodedRef = useRef(onDecoded)
  onDecodedRef.current = onDecoded

  const [cameraLive, setCameraLive] = useState(false)

  /**
   * The live camera could not be acquired. NOT an error state — the photo path
   * below finishes the same task — so it changes the viewfinder's INSTRUCTION
   * rather than raising anything.
   *
   * Distinct from `!cameraLive`, which is also true for the second or so before
   * the stream arrives: "camera unavailable" shown during startup would be a lie
   * that corrects itself, which is worse than saying nothing.
   */
  const [cameraFailed, setCameraFailed] = useState(false)

  /** Message about the last DECODE attempt (the photo path). Rendered inline. */
  const [decodeError, setDecodeError] = useState<string | null>(null)

  const stopCamera = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current)
      rafRef.current = null
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop())
      streamRef.current = null
    }
    if (videoRef.current) {
      // Dropping the srcObject as well as stopping the tracks: a retained
      // srcObject can hold the decoder alive on some builds even with dead tracks.
      ;(videoRef.current as any).srcObject = null
    }
    setCameraLive(false)
  }, [])

  const handleDecoded = useCallback(
    (raw: string) => {
      if (handledRef.current) return
      handledRef.current = true
      stopCamera()
      onDecodedRef.current(raw)
    },
    [stopCamera],
  )

  /**
   * Camera lifecycle, keyed on `open`.
   *
   * The cleanup returned here is what stops the tracks on close AND on unmount —
   * React runs an effect's cleanup in both cases. `UpdatePhoto` (the camera idiom
   * this is modelled on) has no unmount cleanup at all, so navigating away with its
   * camera open leaks the MediaStream and leaves the hardware indicator lit. That
   * bug is deliberately not copied here; see also the mount-scoped backstop below.
   */
  useEffect(() => {
    if (!open) {
      stopCamera()
      return
    }

    handledRef.current = false
    lastDecodeRef.current = 0
    setCameraFailed(false)
    let cancelled = false

    const tick = () => {
      rafRef.current = requestAnimationFrame(tick)

      const now = typeof performance !== 'undefined' ? performance.now() : Date.now()
      if (now - lastDecodeRef.current < DECODE_INTERVAL_MS) return
      lastDecodeRef.current = now

      const video = videoRef.current
      if (!video || video.readyState < HAVE_CURRENT_DATA) return

      if (!canvasRef.current) canvasRef.current = document.createElement('canvas')
      const imageData = drawScaledToImageData(
        video,
        video.videoWidth,
        video.videoHeight,
        canvasRef.current,
      )
      if (!imageData) return

      const raw = decodeImageData(imageData)
      if (raw) handleDecoded(raw)
    }

    const start = async () => {
      try {
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          throw new Error('mediaDevices not supported')
        }

        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' } },
          audio: false,
        })

        // The drawer can be closed while the permission prompt is still up. Without
        // this the stream would be acquired into a component nobody is looking at,
        // and the camera light would stay on.
        if (cancelled) {
          stream.getTracks().forEach(track => track.stop())
          return
        }

        streamRef.current = stream
        setCameraLive(true)

        // Same next-tick assignment as UpdatePhoto: the <video> is only mounted
        // once `cameraLive` flips, so `videoRef` is still null on this line.
        setTimeout(() => {
          if (videoRef.current) {
            ;(videoRef.current as any).srcObject = stream
            videoRef.current.play().catch(() => {})
          }
        }, 0)

        rafRef.current = requestAnimationFrame(tick)
      } catch (_e) {
        if (cancelled) return
        setCameraLive(false)
        /*
         * INLINE, in the viewfinder itself — not a toast.
         *
         * The message's whole content is "use the other input", and the other
         * input is on screen, two rows below. A toast puts that instruction in
         * the one place that scrolls itself away on a timer, over the top of the
         * surface it is talking about; the empty viewfinder is where the user is
         * already looking and is the space the camera would have occupied.
         *
         * Every failure still collapses to ONE message, matching UpdatePhoto:
         * denied, unsupported, no device and insecure-origin are different
         * causes with the same remedy, so it names the remedy rather than
         * diagnosing the camera.
         */
        setCameraFailed(true)
      }
    }

    start()

    return () => {
      cancelled = true
      stopCamera()
    }
  }, [open, handleDecoded, stopCamera])

  /**
   * Unmount backstop. The effect above already cleans up on unmount; this exists so
   * that stays true if its dependencies are ever restructured — a leaked camera is
   * invisible in code review and glaring on a phone (the hardware light stays on).
   * `stopCamera` is idempotent, so running twice costs nothing.
   */
  useEffect(() => () => stopCamera(), [stopCamera])

  const handleFileSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    // Reset so picking the SAME file again still fires a change event — otherwise a
    // failed first attempt cannot be retried with the same photo.
    event.target.value = ''
    if (!file) return
    setDecodeError(null)

    try {
      const img = await loadImageElement(file)
      if (!canvasRef.current) canvasRef.current = document.createElement('canvas')
      const imageData = drawScaledToImageData(
        img,
        img.naturalWidth,
        img.naturalHeight,
        canvasRef.current,
      )
      const raw = imageData ? decodeImageData(imageData) : null

      if (raw) {
        handledRef.current = false
        handleDecoded(raw)
        return
      }

      // Inline, under the control that produced it, rather than a toast — see
      // the camera failure above for why.
      setDecodeError('No code found in that photo. Make sure the whole code is visible.')
    } catch (_e) {
      setDecodeError('That photo could not be read. Try another image.')
    }
  }

  return (
  <Stack spacing={2}>
    {/*
      NO VIEWFINDER AT ALL once the camera is known to be unavailable.

      The black square used to stay, carrying "Camera unavailable. Use Upload from
      photos below." That is the right sentence in the wrong container: a large black
      panel is the shape of a camera preview that has failed to start, so on a desktop
      with no camera at all — where nothing has gone wrong and nothing can be fixed —
      the surface reads as broken. Removing the box removes the claim.

      Gated on `cameraFailed`, NOT on `!cameraLive`. The two differ for the second or so
      before the stream arrives, and hiding the viewfinder during startup would make it
      pop into existence a beat later. While we do not yet know, the box stays and says
      it is starting.
    */}
    {!cameraFailed && (
    <Box
      sx={theme => ({
        position: 'relative',
        width: '100%',
        aspectRatio: '1 / 1',
        overflow: 'hidden',
        borderRadius: `${theme.shape.borderRadius}px`,
        backgroundColor: theme.palette.common.black,
      })}
    >
      {cameraLive ? (
        <video
          ref={videoRef}
          playsInline
          muted
          autoPlay
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      ) : (
        <Stack
          alignItems='center'
          justifyContent='center'
          sx={{ width: '100%', height: '100%', px: 3 }}
        >
          {/*
            STARTUP ONLY. The `cameraFailed` half of this used to live here too; it
            moved out from under the viewfinder because the viewfinder itself is now
            removed in that case (see the gate above), and a message has to live
            somewhere that still renders.
          */}
          <Typography
            variant='body2'
            textAlign='center'
            sx={theme => ({ color: theme.palette.common.white, lineHeight: 1.5 })}
          >
            Starting the camera. If it does not appear, use Upload from photos below.
          </Typography>
        </Stack>
      )}

      {/* The viewfinder frame. Purely an aiming aid: jsQR reads the WHOLE frame,
          so the square is not a crop, it is where a code is most likely to be in
          focus and unskewed. `pointer-events: none` keeps it off the video. */}
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '68%',
          aspectRatio: '1 / 1',
          border: '2px solid rgba(255,255,255,0.9)',
          borderRadius: '12px',
          boxShadow: '0 0 0 9999px rgba(0,0,0,0.28)',
          pointerEvents: 'none',
        }}
      />
    </Box>
    )}

    {/* Paired with the viewfinder, so it goes when the viewfinder goes: "point the
        camera" is not an instruction anyone can follow on a machine that has none. */}
    {!cameraFailed && (
    <Typography
      variant='body2'
      textAlign='center'
      sx={theme => ({ color: theme.palette.text.secondary, lineHeight: 1.5 })}
    >
      Point the camera at their Vera code. Ask them to open Vera, then Profile, then
      My code.
    </Typography>
    )}

    {/* VISIBLE, not tucked behind a failure. On iOS the live path is the one most
        likely to be unavailable, and a code arriving as a screenshot in a chat is
        an ordinary case rather than a fallback — so this sits in the layout as a
        peer of the camera, at the same tap-target sizing as every other sheet row
        (PICKER_SHEET). */}
    <Box
      component='label'
      sx={theme => ({
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
        cursor: 'pointer',
        py: PICKER_SHEET.itemPY,
        px: PICKER_SHEET.itemPX,
        borderRadius: `${PICKER_SHEET.radius}px`,
        border: `1px solid ${theme.palette.grey[400]}`,
        fontSize: PICKER_SHEET.fontSize,
        lineHeight: PICKER_SHEET.lineHeight,
        // Was `text.primary` at rest with a grey[50] hover and no text change. The
        // shared treatment mutes it at rest and adds the brand accent on hover AND
        // keyboard focus — the focus half matters more here than on most rows,
        // because this is a `<label>` wrapping a hidden file input, so a keyboard
        // user reaches it and previously got no visible state at all. The label text
        // is a direct child and the lucide glyph draws in `currentColor`, so both
        // follow the row without extra wiring.
        ...menuRowToneSx(theme),
      })}
    >
      <ImageIcon size={PICKER_SHEET.iconSize} strokeWidth={1} />
      Upload from photos
      <input
        type='file'
        accept='image/*'
        capture='environment'
        hidden
        onChange={handleFileSelected}
      />
    </Box>

    {/*
      The no-camera guidance, BELOW the control it describes.

      Order is the point of this block. With no camera there is exactly one way to
      finish the task, so that control is what the surface should open with rather than
      something reached past an explanation of an absence. The control names itself
      ("Upload from photos"), so it needs no preamble to be understood; what the line
      adds is why the camera is not here and what to ask the other person for, both of
      which are useful AFTER the user knows what they are looking at.
    */}
    {cameraFailed && (
      <Typography
        variant='body2'
        textAlign='center'
        sx={theme => ({ color: theme.palette.text.secondary, lineHeight: 1.5 })}
      >
        No camera on this device. Pick a photo or screenshot of their code instead. Ask
        them to open Vera, then Profile, then My code.
      </Typography>
    )}

    {/*
      ONE message slot for the whole surface, under the control it belongs to.

      Both sources land here — this component's own decode failures and the
      caller's verdict on a decoded code (`notice`) — because from the user's
      side they are the same event: "that didn't work, try again". Two channels
      would mean two places to look and, with the old toast, one of them
      disappearing on a timer.

      The component's own error wins when both are set: it is the more recent
      action, and a stale `notice` from a previous scan should not describe a
      photo that just failed for a different reason.
    */}
    {(decodeError || notice) && (
      <Typography
        role='status'
        variant='body2'
        textAlign='center'
        sx={theme => ({ color: theme.palette.error.main, lineHeight: 1.5 })}
      >
        {decodeError || notice}
      </Typography>
    )}
  </Stack>
  )
}

/**
 * The scanner presented as a bottom sheet — the gift picker's form, where the
 * scan is an interruption of a flow the user is already in and closing it must
 * return them to it untouched.
 */
export const QrScanner = ({ open, onClose, onDecoded, notice }: QrScannerProps) => (
  <DrawerPopup
    title='Scan code'
    handleOpen={open}
    handleClose={onClose}
    child={<QrScannerView active={open} onDecoded={onDecoded} notice={notice} />}
  />
)

export default QrScanner
